/**
 * Render markdown as plain text, for the places that show a body of markdown as
 * a single run of prose rather than a document: a memory row, a contact's About
 * blurb. Strips the common markers — links (including @mention / URN links),
 * emphasis, inline code, and leading heading / list / quote markers — so a
 * reader sees clean text instead of raw syntax.
 */
export const toPlainText = (markdown: string): string =>
  markdown
    .replace(/!\[([^\]]*)\]\(([^)]*)\)/g, (_match, label: string) => label) // ![label](url) -> label
    .replace(/\[([^\]]*)\]\(([^)]*)\)/g, (_match, label: string) => label) // [label](url) -> label
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // **bold** / __bold__
    .replace(/(\*|_)(.*?)\1/g, "$2") // *italic* / _italic_
    .replace(/~~(.*?)~~/g, "$1") // ~~strikethrough~~
    .replace(/`([^`]*)`/g, "$1") // `code`
    .replace(/^\s{0,3}#{1,6}\s+/gm, "") // # headings
    .replace(/^\s*>\s?/gm, "") // > blockquotes
    .replace(/^\s*[-*+]\s+\[[ xX]\]\s+/gm, "") // - [ ] task markers
    .replace(/^\s*[-*+]\s+/gm, "") // - list markers
    .replace(/^\s*\d+[.)]\s+/gm, ""); // 1. list markers

/**
 * Reduce each `[label](url)` link to its label and leave everything else as
 * written, for a one-line preview of text that is not otherwise markdown — a
 * form question. {@link toPlainText} would also read a title's `_` or `*` as
 * emphasis and drop it. Escaped brackets inside a label (`\]`) are allowed and
 * unescaped, and a URL may hold one level of balanced parentheses, as
 * `https://en.wikipedia.org/wiki/Foo_(bar)` does.
 */
export const linkTextOnly = (text: string): string =>
  text.replace(/\[((?:\\.|[^\]\\])*)\]\((?:[^()\s]|\([^()\s]*\))*\)/g, (_match, label: string) =>
    label.replace(/\\([\\[\]])/g, "$1"),
  );
